import { useLocation } from '@reach/router'

/**
 * @typedef {Object} UrlParamsHook
 * @property {function(string, string|boolean): void} updateParam - Atualiza um parâmetro específico na URL
 * @property {function(Object): void} updateParams - Atualiza múltiplos parâmetros na URL de uma vez
 * @property {function(string): string|null} getParam - Obtém o valor de um parâmetro específico da URL
 * @property {function(): Object} getAllParams - Obtém todos os parâmetros da URL como objeto
 * @property {function(string): void} removeParam - Remove um parâmetro específico da URL
 * @property {function(string[]): void} removeParams - Remove múltiplos parâmetros da URL
 * @property {string} pathname - Pathname atual da URL
 * @property {string} search - Search string atual da URL
 */

/**
 * Hook customizado para manipular parâmetros de URL de forma automática
 * @returns {UrlParamsHook} Objeto com funções para manipular parâmetros da URL
 */
export function useUrlParams() {
  const { pathname, search } = useLocation()

  const replaceUrl = newUrl => {
    const currentSearch = search.replace(/^\?/, '')
    const nextSearch = newUrl.split('?')[1] || ''
    if (nextSearch === currentSearch) return

    window.history.replaceState(null, '', newUrl)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  /**
   * Atualiza um parâmetro específico na URL
   * @param {string} paramName - Nome do parâmetro
   * @param {string|boolean|number} paramValue - Valor do parâmetro (remove se vazio: undefined/null/"")
   */
  const updateParam = ({ paramName, paramValue }) => {
    const urlParams = new URLSearchParams(search)

    const shouldRemove = paramValue === undefined || paramValue === null || paramValue === ''
    if (!shouldRemove) {
      urlParams.set(paramName, String(paramValue))
    } else {
      urlParams.delete(paramName)
    }

    const newSearch = urlParams.toString()
    const newUrl = newSearch ? `${pathname}?${newSearch}` : pathname
    replaceUrl(newUrl)
  }

  /**
   * Atualiza múltiplos parâmetros na URL de uma vez
   * @param {Object} params - Objeto com os parâmetros a serem atualizados
   */
  const updateParams = params => {
    const urlParams = new URLSearchParams(search)

    Object.entries(params).forEach(([key, value]) => {
      const shouldRemove = value === undefined || value === null || value === ''
      if (!shouldRemove) {
        urlParams.set(key, String(value))
      } else {
        urlParams.delete(key)
      }
    })

    const newSearch = urlParams.toString()
    const newUrl = newSearch ? `${pathname}?${newSearch}` : pathname
    replaceUrl(newUrl)
  }

  /**
   * Obtém o valor de um parâmetro específico da URL
   * @param {string} paramName - Nome do parâmetro
   * @returns {string|null} Valor do parâmetro ou null se não existir
   */
  const getParam = paramName => {
    const urlParams = new URLSearchParams(search)
    return urlParams.get(paramName)
  }

  /**
   * Obtém todos os parâmetros da URL como objeto
   * @returns {Object} Objeto com todos os parâmetros da URL
   */
  const getAllParams = () => {
    const urlParams = new URLSearchParams(search)
    const params = {}

    for (const [key, value] of urlParams.entries()) {
      params[key] = value
    }

    return params
  }

  /**
   * Remove um parâmetro específico da URL
   * @param {string} paramName - Nome do parâmetro a ser removido
   */
  const removeParam = paramName => {
    updateParam({ paramName, paramValue: null })
  }

  /**
   * Remove múltiplos parâmetros da URL
   * @param {string[]} paramNames - Array com os nomes dos parâmetros a serem removidos
   */
  const removeParams = paramNames => {
    const urlParams = new URLSearchParams(search)

    paramNames.forEach(paramName => {
      urlParams.delete(paramName)
    })

    const newSearch = urlParams.toString()
    const newUrl = newSearch ? `${pathname}?${newSearch}` : pathname
    replaceUrl(newUrl)
  }

  return {
    updateParam,
    updateParams,
    getParam,
    getAllParams,
    removeParam,
    removeParams,
    pathname,
    search,
  }
}
